<template>
  <div class="question-result-wrapper">
    <!-- Modern Header -->
    <div class="result-header">
      <div class="header-content">
        <AppQuestionProgress
          :questionNo="questionNo"
          :questionCount="10"
          :showButton="true"
          :hideNavigation="hideNavigation"
          :completeData="false"
          :onToogleResult="true"
          :branchId="branchId"
          :groups="groups"
          :answers="answers"
          :currentQuestionId="3"
        />

        <div class="header-actions">
          <button class="btn btn-back" @click="back" :disabled="questionNo == 0">
            <b-icon-chevron-left></b-icon-chevron-left>
            <span class="btn-text">Back</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Container -->
    <div class="result-content-container">
      <!-- No Results State -->
      <div v-if="!$store.getters.getShowCardComponents" class="no-results-state">
        <div class="no-results-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <h3 class="no-results-title">No Automated Decision Support Available</h3>
        <p class="no-results-text">No automated decision support available for your selection.</p>
      </div>

      <!-- Results Available -->
      <div v-else class="results-section">
        <div class="results-header">
          <h2 class="results-title">
            <span class="when-opened mr-2">
              <b-icon icon="check2-circle" />
            </span>
            Result
          </h2>

          <!-- ✅ UPDATED: Treatment Priority Controls - Now shows for Oncology, Antibiotic, AND Emergency -->
          <div v-if="shouldShowPriorityControls" class="treatment-priority-controls">
            <div class="priority-label">Treatment Priority:</div>
            <div class="priority-options">
              <div class="custom-control custom-radio mr-3">
                <input
                  id="useEfficacyRadio"
                  name="opinionRadio"
                  :value="true"
                  class="custom-control-input"
                  type="radio"
                  v-model="efficacyFirst"
                  @change="handleRadioChange(true)"
                />
                <label class="custom-control-label" for="useEfficacyRadio">
                  <svg class="priority-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                  </svg>
                  efficacy
                </label>
              </div>

              <div class="custom-control custom-radio">
                <input
                  id="useSafetyRadio"
                  name="opinionRadio"
                  :value="false"
                  class="custom-control-input"
                  type="radio"
                  v-model="efficacyFirst"
                  @change="handleRadioChange(false)"
                />
                <label class="custom-control-label" for="useSafetyRadio">
                  <svg class="priority-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z"/>
                  </svg>
                  safety
                </label>
              </div>
            </div>
          </div>
          
        </div>

        <!-- Navigation Buttons - Shows for Oncology, Antibiotic, and Emergency -->
        <div v-if="shouldShowNavigation && treatments.length > 0" class="result-navigation">
          <button
            v-if="$store.getters.getCurrentIndex > 0"
            @click="decreaseIndex()"
            class="btn-navigation btn-previous"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span class="btn-full-text">Show me the previous option</span>
            <span class="btn-short-text">Prev</span>
          </button>

          <button
            v-if="treatments[$store.getters.getCurrentIndex]?.possibleTreatments > 1"
            @click="increaseIndex()"
            class="btn-navigation btn-next"
          >
            <span class="btn-full-text">Show me the next best option</span>
            <span class="btn-short-text">Next</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Spotlight Label -->
        <div class="result-spotlight">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Recommended Treatment
        </div>

        <!-- Result Card Display -->
        <div class="result-card-wrapper">
          <ResultCard
            v-if="treatments.length > 0 && treatments[$store.getters.getCurrentIndex]"
            :resultIndex="$store.getters.getCurrentIndex + 1"
            :treatmentName="treatments[$store.getters.getCurrentIndex].name"
            :treatmentPercent="treatments[$store.getters.getCurrentIndex].percent"
            :toolBranchId="treatments[$store.getters.getCurrentIndex].toolBranchId"
            :treatmentNotes="treatments[$store.getters.getCurrentIndex].notes"
            :smpcLink="treatments[$store.getters.getCurrentIndex].links.smpcLink"
            :publications="treatments[$store.getters.getCurrentIndex].links.publications"
            :branchId="branchId"
            :departmentType="departmentType"
          />
        </div> <!-- Close result-card-wrapper -->

        <!-- Action Buttons — moved below the result card -->
        <div class="action-buttons">
          <button @click="showDoctorSelectionModal" class="btn btn-danger">
            <img src="../../assets/group1.png" alt="Doctor opinions icon" />
            See Other Opinions
          </button>
        </div>
      </div> <!-- Close results-section -->
    </div> <!-- Close result-content-container -->

    <!-- Modals -->
    <DoctorSelectionModal 
      v-if="showDoctorModal"
      :showModal="showDoctorModal"
      :branchId="branchId"
      :departmentType="departmentType"
      :isOtherOpinions="true"
      @doctor-selected="onDoctorSelected"
      @modal-closed="closeDoctorModal"
    />

    <report-button
      v-if="!showDoctorModal"
      questionText="Did you find any mistake in the recommendations?"
      callToActionText="Let us know!"
      @click.native="showModal" 
      class=""
    />

    <feedback-modal 
      :modalVisible.sync="modalVisible"
      :answers.sync="answers" 
      :treatmentHistory.sync="treatmentHistory"
      :excludedRecommendations.sync="excludedRecommendations"
      :branchId.sync="branchId"
    />
    
    <download-report-modal
      :downloadModalVisible.sync="downloadModalVisible"
      :answers.sync="answers" 
      :treatmentHistory.sync="treatmentHistory"
      :excludedRecommendations.sync="excludedRecommendations"
      :branchId.sync="branchId"
      :result.sync="localResult"
    />
  </div> <!-- Close question-result-wrapper -->
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { QuestionnaireResult } from "@/entities/questionaire/QuestionnaireResult";
import { Group } from "@/entities/questionaire/Group";
import { Answer } from "@/entities/questionaire/answers/Answer";
import AppProgressBar from "@/components/AppProgressBar.vue";
import AppQuestionProgress from "@/components/questionnaire/AppQuestionProgress.vue";
import { HttpHandler } from "@/http/HttpHandler";
import { Treatment } from "@/entities/Treatment";
import ReportButton from '../UI/ReportButton.vue';
import DownloadReportModal from '../UI/DownloadReportModal.vue';
import FeedbackModal from '../UI/FeedbackModal.vue';
import SimpleButton from '../UI/SimpleButton.vue';
import ResultCard from '../UI/ResultCard.vue';
import store from "@/store/index";
import { TreatmentResult } from "@/store/TreatmentResult"; 
import { Links, Publication } from '@/entities/questionaire/Links';
import DoctorSelectionModal from "@/components/DoctorSelectionModal.vue";
import { analytics } from '@/services/analytics';

interface Recommendation {
  tool: {
    id: number;
    name: string;
  };
}

@Component({
  components: {
    ProgressBar: AppProgressBar,
    ReportButton,
    FeedbackModal,
    SimpleButton,
    DownloadReportModal,
    AppQuestionProgress,
    ResultCard,
    DoctorSelectionModal,
  },
})
export default class AppQuestionResult extends Vue {
  
  private httpHandler: HttpHandler = new HttpHandler(store);
  private progress: number = 0;
  private progressColor: string = "";
  private progressText: string = "";
  public efficacyFirst: boolean = true;
  public excludedRecommendations: { id: number; name: string }[] = [];
  public count: number = 0;
  private completionCalled: boolean = false;
  public modalVisible: Boolean = false;
  public downloadModalVisible: Boolean = false;
  public treatments: TreatmentResult[] = [];
  private localRecommendations: Recommendation[] = [];
  private currentTreatment: Treatment | null = null;
  public clickInc: boolean = true;
  public clickDec: boolean = false;
  public localTreatmentName: string = '';
  public localTreatmentPercent: any = '';
  public localTreatmentNotes: string[] = [];
  public localSmpcLink: string = '';
  public localPublications: Publication[] = [];
  public showDoctorModal: boolean = false;
  startTime: number = 0;
  hasTrackedStart: boolean = false;
  branchName: string = '';
  departmentType: string = '';  // Store departmentType from API
  
  // ✅ Define allowed departments for both navigation AND priority controls
  allowedDepartmentsForNavigation: string[] = ['Oncology', 'antibiotic'];
  allowedDepartmentsForPriorityControls: string[] = ['Oncology', 'antibiotic'];
 
  @Prop() recommendations!: Recommendation[];
  @Prop() hideNavigation!: boolean | false;
  @Prop() actRecommendations!: {
    id: number;
    name: string;
    approvalPercentage: number;
  }[];
  @Prop() branchId!: number | null;
  @Prop() groups!: Group[] | null;
  @Prop() answers!: Answer[] | null;
  @Prop() treatmentHistory!: Treatment[] | null;
  @Prop() useOnlyMyOpinion!: boolean | null;
  @Prop() hideSafetySelector!: boolean | null;
  @Prop({ default: false }) completeData!: boolean;
  @Prop() questionNo!: number | 1;
  @Prop() smpcLink!: string | null; 
  @Prop() publications!: string[]; 
  @Prop() result!: QuestionnaireResult | null;
  
  public localResult: QuestionnaireResult = new QuestionnaireResult([], []);
  public localCompleteData: boolean = false;

  back(): void {
    this.excludedRecommendations = [];
    this.$events.emit("onSetPreviousQuestion");
  }

  private async fetchBranchName(branchId: number): Promise<string> {
    try {
      const branch = await this.httpHandler.getBranchById(branchId);
      console.log('Branch data:', branch);
      
      // Try to get departmentType from API first
      const apiDepartmentType = (branch as any).departmentType;
      
      if (apiDepartmentType) {
        // Use API departmentType if available
        this.departmentType = apiDepartmentType;
        console.log('Department type set from API:', this.departmentType);
      } else {
        // Fallback: Extract first part from branch name
        const branchName = branch.name || '';
        if (branchName.includes('/')) {
          this.departmentType = branchName.split('/')[0].trim();
        } else {
          this.departmentType = branchName;
        }
        console.log('Department type fallback:', this.departmentType);
      }
      
      return branch.name || `Branch ${branchId}`;
    } catch (error) {
      console.warn('Could not fetch branch name:', error);
      return `Branch ${branchId}`;
    }
  }

  async excludeRecommendation(
    treat: { id: number; name: string },
    approvalPercentage: number = 100
  ) {
    let alreadyAdded = this.excludedRecommendations.find((t) => t.id === treat.id);
    if (!alreadyAdded) {
      const treatment = {
        id: treat.id,
        name: treat.name,
        approvalPercentage: approvalPercentage,
      };
      this.excludedRecommendations.push(treatment);
      await this.updateResults();
      this.count++;
    }
  }

  async handleRadioChange(value: boolean) {
    this.excludedRecommendations = [];
    const treatment = this.treatments[this.$store.getters.getCurrentIndex];
    this.$store.commit('setDeleteStore', false);
    await this.updateResults();
    this.handleRadioClick(value);
  }

  handleRadioClick(value: boolean): void {
    console.log(`Radio-Button wurde mit dem Wert ${value ? 'true' : 'false'} geklickt.`);
  }

  async includeRecommendation(treat: { id: number; name: string }) {
    let alreadyAdded = this.excludedRecommendations.find((t) => t.id === treat.id);
    if (alreadyAdded) {
      const index = this.excludedRecommendations.findIndex(rec => rec.id === treat.id);
      if (index >= 0) {
        this.excludedRecommendations.splice(index, 1);
      }
      await this.updateResults();
    }
  }

  async updateResults(): Promise<void> {
    let answers = this.answers?.map((x) => x.getQuizResultObject()) || [];
    let treatmentHistory = this.treatmentHistory?.filter((h) => h.id != 0) || [];

    if (!this.branchId || !this.groups || this.groups.length < 1 || 
        (answers.length === 0 && treatmentHistory.length === 0)) {
      this.localResult = new QuestionnaireResult([], []);
      return;
    }

    const properties = [...this.groups]
      .map((x) => x.properties)
      .reduce((a, b) => a.concat(b))
      .filter(x => !x.isHidden);

    if (this.$store.getters.getDeleteExcludeRecommendations == true) {
      this.$store.commit('setDeleteExcludeRecommendations', false);
      this.excludedRecommendations = [];
    }
    
    if (!this.branchName && this.branchId) {
     this.branchName = await this.fetchBranchName(this.branchId);
    }

    const answeredCount = this.answers?.length || 0;
    const totalQuestions = properties.length;
    
    if (answeredCount > 0 && this.branchId) {
      const lastAnswer = this.answers![answeredCount - 1];
      const property = properties.find(p => p.id === lastAnswer.PropertyId);
      
      if (property) {
        analytics.trackQuestionAnswered(
          this.branchId,
          this.branchName,     
          answeredCount,
          property.id,
          property.question,
          totalQuestions
        );
      }
    }
    

    const quizResults = {
      answers: answers,
      treatmentHistory: treatmentHistory,
      excludedRecommendations: this.excludedRecommendations.map((t) => t.id),
    };

    const identifier = this.$store.getters['questionnaire/getQuestionnaireIdentifier'];

    this.localResult = await this.httpHandler.postQuizResultObjectsByBranchId(
      this.branchId,
      quizResults,
      this.useOnlyMyOpinion,
      !this.efficacyFirst,
      identifier
    );

    let answersLength = this.answers?.length || 0;
    this.progress = (answersLength * 100) / properties.length;

    if (properties.some((x) => x.isPrimary && this.answers?.every((y) => y.PropertyId != x.id))) {
      this.progressColor = "#D9544F";
      this.progressText = "Data Incomplete";
      this.localCompleteData = false;
    } else if (answersLength < properties.length) {
      this.progressColor = "#EFAD4D";
      this.progressText = "Sufficient Data";
      this.localCompleteData = false;
    } else {
      this.progressColor = this.localResult.recommendations.length ? "#5DB75D" : "#D9544F";
      this.progressText = "Data Complete";
      this.localCompleteData = true;

      if (this.startTime && this.branchId) {
        const durationSeconds = Math.round((Date.now() - this.startTime) / 1000);
        
        analytics.trackQuestionnaireCompleted(
          this.branchId,
          this.branchName,     
          totalQuestions,
          answersLength,
          durationSeconds
        );
      }
    }

    if (this.branchId) {
      const dataCompleteness = 
        this.progressText === "Data Incomplete" ? 'incomplete' :
        this.progressText === "Sufficient Data" ? 'sufficient' : 'complete';
      
      analytics.trackRecommendationsViewed(
        this.branchId,
        this.branchName,
        this.localResult.recommendations?.length || 0,
        dataCompleteness
      );
    }
  }

  private async completeQuestionnaireOnExit() {
    const identifier = this.$store.getters['questionnaireModule/getQuestionnaireIdentifier'];
    if (identifier) {
      try {
        await this.httpHandler.completeQuestionnaire(identifier, {
          useOnlyMyOpinion: this.useOnlyMyOpinion,
          safetyFirst: !this.efficacyFirst,
          branchId: this.branchId
        });
        
        this.$store.commit('questionnaireModule/CLEAR_QUESTIONNAIRE_IDENTIFIER');
        console.log('Questionnaire completed and identifier cleared from store');
        
      } catch (error) {
        console.error('Failed to complete questionnaire on exit:', error);
      }
    }
  }

  get showCardComponents(): boolean {
    return this.$store.getters.getShowCardComponents;
  }

  // ✅ KEEP: Specific check for oncology department (backward compatibility)
  get isOncologyDepartment(): boolean {
    console.log('Checking oncology department, departmentType:', this.departmentType);
    const dept = (this.departmentType || '').toLowerCase().trim();
    return dept.includes('oncology') || dept.includes('onkologie');
  }

  // ✅ NEW: Check if navigation should be shown (Oncology, Antibiotic, Emergency)
  get shouldShowNavigation(): boolean {
    console.log('Checking shouldShowNavigation, departmentType:', this.departmentType);
    const dept = (this.departmentType || '').toLowerCase().trim();
    const shouldShow = this.allowedDepartmentsForNavigation.some(
      allowed => dept.includes(allowed.toLowerCase())
    );
    console.log('shouldShowNavigation result:', shouldShow);
    return shouldShow;
  }

  // ✅ NEW: Check if priority controls should be shown (Oncology, Antibiotic, Emergency)
  get shouldShowPriorityControls(): boolean {
    console.log('Checking shouldShowPriorityControls, departmentType:', this.departmentType);
    const dept = (this.departmentType || '').toLowerCase().trim();
    const shouldShow = this.allowedDepartmentsForPriorityControls.some(
      allowed => dept.includes(allowed.toLowerCase())
    );
    console.log('shouldShowPriorityControls result:', shouldShow);
    return shouldShow;
  }

  async mounted() {
    if (!this.hasTrackedStart && this.branchId) {
      this.branchName = await this.fetchBranchName(this.branchId);
      this.startTime = Date.now();
      this.hasTrackedStart = true;
      analytics.trackQuestionnaireStarted(this.branchId, this.branchName);
    }
    
    this.$events.on("goToBranch", this.goToBranch);
  }

  beforeDestroy() {
    this.$events.off("goToBranch");
    this.completeQuestionnaireOnExit();
  }

  goToBranch(toolBranchId: number) {
    console.log("Navigating from App Question Result to branch with ID:", toolBranchId);
    this.$events.emit("goToMainBranch", toolBranchId);
  }

  showDoctorSelectionModal(): void {
    console.log('=== Opening Doctor Modal ===');
    console.log('branchId:', this.branchId);
    console.log('branchName:', this.branchName);
    console.log('departmentType:', this.departmentType);
    this.showDoctorModal = true;
  }

  onDoctorSelected(selectedDoctor: any): void {
    console.log('Doctor selected:', selectedDoctor);
    this.closeDoctorModal();
  }

  closeDoctorModal(): void {
    this.showDoctorModal = false;
  }

  @Watch('$store.state.result.dynamicResultIndex')
  onDynamicResultIndexChanged(newValue: number, oldValue: number) {
    this.getTreatments(newValue);
  }

  getTreatments(index: number): void {
    const treatments: TreatmentResult[] = this.$store.getters.getTreatmentsByIndices([index]);
  }

  @Watch("$store.state.result.treatments", { immediate: true, deep: true })
  onTreatmentsChanged(newTreatments: TreatmentResult[] | undefined, oldTreatments: TreatmentResult[] | undefined) {
    if (newTreatments && newTreatments.length > 0) {
      this.treatments = newTreatments;
    }
  }

  @Watch("answers")
  onAnswersChanged() {
    this.updateResults();
  }

  @Watch("treatmentHistory", { deep: true })
  onTreatmentHistoryChanged() {
    let emptyTreatment = this.treatmentHistory?.find((t) => t.id === 0);
    if (emptyTreatment === undefined) {
      this.updateResults();
    }
  }

  @Watch("useOnlyMyOpinion")
  onUseOnlyMyOpinionChanged() {
    this.updateResults();
  }

  @Watch("efficacyFirst")
  onEfficacyFirstChanged() {
    this.updateResults();
  }

  @Watch("result")
  onResultChanged() {
    this.localResult = this.result ?? new QuestionnaireResult([], []);
  }

  @Watch("localResult")
  onLocalResultChanged() {
    this.$emit("update:result", this.localResult);
  }

  @Watch("localCompleteData")
  onLocalCompleteDataChanged() {
    this.$emit("update:completeData", this.localCompleteData);
  }

  showModal(): void {
    this.modalVisible = true;
  }

  showDownloadModal(): void {
    this.downloadModalVisible = true;
  }

  public increaseIndex(): void {
    const currentIndex = this.$store.getters.getCurrentIndex;
    console.log("Current Index: ", currentIndex);
    console.log("Current pT:", this.treatments[this.$store.getters.getCurrentIndex].possibleTreatments);

    if (this.treatments[this.$store.getters.getCurrentIndex].possibleTreatments > 1) {
      this.$store.commit('setDeleteStore', false);
      const treatment = this.treatments[this.$store.getters.getCurrentIndex];
      
      if (treatment) {
        this.$store.commit('incrementIndex');
        this.excludeRecommendation(treatment);
      } else {
        console.error("Treatment is undefined.");
      }
    }
  }

  public decreaseIndex(): void {
    if (this.$store.getters.getCurrentIndex > 0) {
      this.$store.commit('setDeleteStore', false);
      this.$store.commit('decrementIndex');
      
      console.log("Index: ", this.$store.getters.getCurrentIndex);
      const treatment = this.treatments[this.$store.getters.getCurrentIndex]; 
      
      if (treatment) {
        this.updateTreatmentUI(treatment);
      }    
    }
  }

  private updateTreatmentUI(treatment: TreatmentResult): void {
    this.localTreatmentName = treatment.name;
    this.localTreatmentPercent = treatment.percent;
    this.localTreatmentNotes = treatment.notes;
    this.localSmpcLink = treatment.links.smpcLink;
    this.localPublications = treatment.links.publications;
  }
}
</script>

<style scoped lang="scss">

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');


/* Treatment Priority Controls Styling */
.treatment-priority-controls {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.05), rgba(47, 69, 165, 0.03));
  border: 1px solid rgba(102, 123, 242, 0.15);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.priority-label {
  font-weight: 700;
  color: #2f45a5;
  font-size: 1rem;
  white-space: nowrap;
  margin: 0;
}

.priority-options {
  display: flex;
  gap: 3rem;
  flex: 1;
  align-items: center;
}

.custom-control {
  display: flex;
  align-items: center;
  position: relative;
  padding: 0;
  margin: 0;
}

.custom-control-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 20px;
  height: 20px;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.custom-control-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #475569;
  font-weight: 500;
  font-size: 0.95rem;
  padding-left: 28px;
  margin: 0;
  transition: all 0.3s ease;
  position: relative;
  user-select: none;
  line-height: 20px;
}

/* Radio button circle - UNCHECKED */
.custom-control-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  background: white;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Radio button inner dot - NOT USED IN CHECKED STATE */
.custom-control-label::after {
  display: none;
}

/* Checked state - FILLED CIRCLE */
.custom-control-input:checked + .custom-control-label::before {
  border-color: #2f45a5;
  background: #2f45a5;
  box-shadow: 0 0 0 3px rgba(47, 69, 165, 0.15);
}

/* Add white checkmark or dot for checked state */
.custom-control-input:checked + .custom-control-label::after {
  content: '';
  display: block;
  position: absolute;
  left: 7px;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: white;
}

.custom-control-input:checked + .custom-control-label {
  color: #2f45a5;
  font-weight: 600;
}

/* Hover state */
.custom-control-label:hover::before {
  border-color: #667bf2;
  box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.1);
}

.custom-control-label:hover {
  color: #2f45a5;
}

/* Icon styling */
.priority-icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.custom-control-input:checked + .custom-control-label .priority-icon {
  color: #2f45a5;
  transform: scale(1.1);
}

.custom-control-label:hover .priority-icon {
  color: #667bf2;
}

/* Remove default Bootstrap margins */
.mr-3 {
  margin-right: 0 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .treatment-priority-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
  
  .priority-label {
    width: 100%;
    margin-bottom: 0.5rem;
  }
  
  .priority-options {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
  
  .custom-control {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .priority-options {
    gap: 0.75rem;
  }
  
  .custom-control-label {
    font-size: 0.9rem;
  }
}



.question-result-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f8fafc;
 // height: 100vh;
  position: relative;
  overflow: hidden;
}

/* Header Section */
.result-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1.5rem 2rem;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: rgba(102, 123, 242, 0.1);
  color: #2f45a5;
  border: 2px solid rgba(102, 123, 242, 0.2);
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: rgba(102, 123, 242, 0.15);
    border-color: rgba(102, 123, 242, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.result-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border-radius: 12px;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(47, 69, 165, 0.2);

  svg {
    width: 20px;
    height: 20px;
  }
}

/* Main Content */
.result-content-container {
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 2rem;
  //height: calc(100vh - 200px);
  overflow: hidden;
}


/* No Results State */
.no-results-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.no-results-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.1), rgba(47, 69, 165, 0.05));
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 40px;
    height: 40px;
    color: #667bf2;
  }
}

.no-results-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.no-results-text {
  color: #64748b;
  font-size: 1rem;
}

/* Results Section */
.results-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  height: 100%;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 3rem;
}

.results-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;

  svg {
    width: 28px;
    height: 28px;
    color: #667bf2;
  }
}

/* Spotlight label — hidden on desktop, shown on mobile */
.result-spotlight {
  display: none;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #34d399);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
  }
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444, #f87171);
  color: white;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
  }
}

.result-card-wrapper {
  margin-top: 2rem;
}

/* Feedback Button */
.feedback-button {
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  max-width: 400px;
  z-index: 90;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .result-header {
    padding: 1rem;
  }

  .header-actions {
    flex-direction: column;
    gap: 1rem;
  }

  .result-badge {
    width: 100%;
    justify-content: center;
  }

  .result-content-container {
    padding: 0 1rem;
  }

  .results-section {
    padding: 1.5rem;
  }

  .results-header {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    gap: 0.5rem;
  }

  .results-title {
    font-size: 0.875rem;
    gap: 0.25rem;
    flex-shrink: 0;

    .when-opened {
      font-size: 0.9rem;
      margin-right: 0 !important;
    }
  }

  /* Spotlight label — visible on mobile */
  .result-spotlight {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: linear-gradient(135deg, #10b981, #34d399);
    color: white;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
  }

  .action-buttons {
    width: 100%;
    justify-content: center;
    margin-top: 1.25rem;
    margin-left: 0;

    .btn {
      flex: 1;
      justify-content: center;
    }
  }

  .no-results-state {
    padding: 3rem 1.5rem;
  }

  .no-results-title {
    font-size: 1.25rem;
  }

  .feedback-button {
    left: 1rem;
    right: 1rem;
    max-width: calc(100% - 2rem);
  }
}

@media (max-width: 480px) {
  .result-header {
    padding: 0.75rem;
  }

  .btn-back {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;

    .btn-text {
      display: none;
    }
  }

  .result-badge {
    padding: 0.625rem 1.25rem;
    font-size: 0.9rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .results-section {
    padding: 1rem;
  }

  .results-title {
    font-size: 1.1rem;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .action-buttons {
    flex-direction: column;
    
    .btn {
      width: 100%;
      min-width: auto;
    }
  }

  .btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.9rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .no-results-state {
    padding: 2rem 1rem;
  }

  .no-results-icon {
    width: 60px;
    height: 60px;

    svg {
      width: 30px;
      height: 30px;
    }
  }

  .no-results-title {
    font-size: 1.1rem;
  }

  .no-results-text {
    font-size: 0.95rem;
  }


  /* Result Navigation Buttons (Oncology Only) */
.result-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 249, 0.6));
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.5);
}

.btn-navigation {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.btn-navigation svg {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.btn-previous {
  background: white;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-previous:hover {
  background: #f8fafc;
  color: #2f45a5;
  border-color: #cbd5e1;
  transform: translateX(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-previous:hover svg {
  transform: translateX(-2px);
}

.btn-next {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  margin-left: auto;
}

.btn-next:hover {
  transform: translateX(3px);
  box-shadow: 0 4px 16px rgba(47, 69, 165, 0.3);
}

.btn-next:hover svg {
  transform: translateX(2px);
}

.btn-navigation:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Responsive */
@media (max-width: 768px) {
  .result-navigation {
    flex-direction: column;
    gap: 0.75rem;
  }

  .btn-navigation {
    width: 100%;
    justify-content: center;
  }

  .btn-next {
    margin-left: 0;
  }

  .btn-previous {
    order: 2;
  }

  .btn-next {
    order: 1;
  }
}
}

/* Navigation button text visibility */
.btn-short-text {
  display: none;
}

.btn-full-text {
  display: inline;
}

/* Treatment Priority Controls Styling */
.treatment-priority-controls {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.05), rgba(47, 69, 165, 0.03));
  border: 1px solid rgba(102, 123, 242, 0.15);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.priority-label {
  font-weight: 700;
  color: #2f45a5;
  font-size: 1rem;
  white-space: nowrap;
  margin: 0;
}

.priority-options {
  display: flex;
  gap: 3rem;
  flex: 1;
  align-items: center;
}

.custom-control {
  display: flex;
  align-items: center;
  position: relative;
  padding: 0;
  margin: 0;
}

.custom-control-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 20px;
  height: 20px;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.custom-control-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #475569;
  font-weight: 500;
  font-size: 0.95rem;
  padding-left: 28px;
  margin: 0;
  transition: all 0.3s ease;
  position: relative;
  user-select: none;
  line-height: 20px;
}

/* Radio button circle - UNCHECKED */
.custom-control-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  background: white;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Radio button inner dot */
.custom-control-label::after {
  display: none;
}

/* Checked state - FILLED CIRCLE */
.custom-control-input:checked + .custom-control-label::before {
  border-color: #2f45a5;
  background: #2f45a5;
  box-shadow: 0 0 0 3px rgba(47, 69, 165, 0.15);
}

/* White dot for checked state */
.custom-control-input:checked + .custom-control-label::after {
  content: '';
  display: block;
  position: absolute;
  left: 7px;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: white;
}

.custom-control-input:checked + .custom-control-label {
  color: #2f45a5;
  font-weight: 600;
}

/* Hover state */
.custom-control-label:hover::before {
  border-color: #667bf2;
  box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.1);
}

.custom-control-label:hover {
  color: #2f45a5;
}

/* Icon styling */
.priority-icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.custom-control-input:checked + .custom-control-label .priority-icon {
  color: #2f45a5;
  transform: scale(1.1);
}

.custom-control-label:hover .priority-icon {
  color: #667bf2;
}

/* Remove default Bootstrap margins */
.mr-3 {
  margin-right: 0 !important;
}

/* Result Navigation Buttons (Oncology Only) */
.result-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  //margin-top: 2rem;
  //padding: 1rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 249, 0.6));
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.5);
}

.btn-navigation {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.btn-navigation svg {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.btn-previous {
  background: white;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-previous:hover {
  background: #f8fafc;
  color: #2f45a5;
  border-color: #cbd5e1;
  transform: translateX(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-previous:hover svg {
  transform: translateX(-2px);
}

.btn-next {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  margin-left: auto;
}

.btn-next:hover {
  transform: translateX(3px);
  box-shadow: 0 4px 16px rgba(47, 69, 165, 0.3);
}

.btn-next:hover svg {
  transform: translateX(2px);
}

.btn-navigation:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .treatment-priority-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.4rem 0.6rem;
    margin: 0;
    min-width: 0;
  }

  .priority-label {
    font-size: 0.75rem;
    margin-bottom: 0;
  }

  .priority-options {
    flex-direction: row;
    gap: 0.5rem;
    width: auto;
  }

  .custom-control {
    width: auto;
  }

  .custom-control-label {
    font-size: 0.75rem;
    gap: 0.2rem;
  }

  .result-navigation {
    flex-direction: row;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    margin-top: 0.5rem;
  }

  .btn-navigation {
    width: auto;
    padding: 0.625rem 0.875rem;
    font-size: 0.85rem;
  }

  .btn-next {
    margin-left: auto;
  }

  .btn-full-text {
    display: none;
  }

  .btn-short-text {
    display: inline;
  }
}

@media (max-width: 480px) {
  .priority-options {
    gap: 1rem;
  }

  .custom-control-label {
    font-size: 0.9rem;
  }
}


</style>